import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import type { Firestore } from 'firebase/firestore'
import type { Functions } from 'firebase/functions'
import type { FirebaseStorage } from 'firebase/storage'

import { getActiveEnvConfig } from '../config/env.config'

const { firebase: firebaseConfig, appCheckDebugToken } = getActiveEnvConfig()

export const app: FirebaseApp = initializeApp(firebaseConfig)
export const auth: Auth = getAuth(app)

const appCheckInitPromise = (async (): Promise<void> => {
  const { initializeAppCheck, ReCaptchaEnterpriseProvider } = await import('firebase/app-check')

  if (appCheckDebugToken) {
    // @ts-expect-error: Setting debug token for App Check in development
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken
  }

  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(firebaseConfig.recaptchaSiteKey || ''),
    isTokenAutoRefreshEnabled: true,
  })
})()

/** Resolves when App Check is ready. Required before Auth when App Check enforcement is enabled. */
export const ensureAppCheck = (): Promise<void> => appCheckInitPromise

const scheduleAnalytics = (): void => {
  const run = () => {
    void (async () => {
      const { getAnalytics, isSupported } = await import('firebase/analytics')
      if (await isSupported()) {
        getAnalytics(app)
      }
    })()
  }

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run)
  } else {
    setTimeout(run, 1)
  }
}

scheduleAnalytics()

let dbInstance: Firestore | null = null
let dbPromise: Promise<Firestore> | null = null

export const getDb = async (): Promise<Firestore> => {
  if (dbInstance) {
    return dbInstance
  }

  if (!dbPromise) {
    dbPromise = (async () => {
      await ensureAppCheck()
      const { getFirestore } = await import('firebase/firestore')
      dbInstance = getFirestore(app)
      return dbInstance
    })()
  }

  return dbPromise
}

let functionsInstance: Functions | null = null
let functionsPromise: Promise<Functions> | null = null

export const getFirebaseFunctions = async (): Promise<Functions> => {
  if (functionsInstance) {
    return functionsInstance
  }

  if (!functionsPromise) {
    functionsPromise = (async () => {
      await ensureAppCheck()
      const { getFunctions } = await import('firebase/functions')
      functionsInstance = getFunctions(app)
      return functionsInstance
    })()
  }

  return functionsPromise
}

let storageInstance: FirebaseStorage | null = null
let storagePromise: Promise<FirebaseStorage> | null = null

export const getFirebaseStorage = async (): Promise<FirebaseStorage> => {
  if (storageInstance) {
    return storageInstance
  }

  if (!storagePromise) {
    storagePromise = (async () => {
      await ensureAppCheck()
      const { getStorage } = await import('firebase/storage')
      storageInstance = getStorage(app)
      return storageInstance
    })()
  }

  return storagePromise
}
